import { RequestError } from './httpClient.ts';

/**
 * Whether a thrown request failure is worth retrying: only 5xx and
 * network/timeout errors. Skips syntax errors, 4xx, deterministic config/
 * programming errors (e.g. the allowlist guard), and non-`RequestError` throws
 * (e.g. `UserError`s) — a replay won't change those.
 *
 * `httpRequest` throws 5xx rather than returning it, keeping the status only in
 * a Response or the "unexpected status <code>" message, so we read it there.
 */
export const isTransientRequestFailure = (error: unknown): boolean => {
  if (!(error instanceof RequestError) || error.errorType === 'syntax') {
    return false;
  }
  // A thrown Response carries the real status directly.
  if (error.response) {
    return error.response.status >= 500;
  }
  // Otherwise the status, if any, lives only in the message.
  const statusMatch = /unexpected status\s+(\d{3})\b/i.exec(error.message);
  if (statusMatch) {
    return Number(statusMatch[1]) >= 500;
  }
  // No status token: retry only genuine network/timeout failures. Everything
  // else here (e.g. the allowlist guard) is a deterministic programming/config
  // error a replay can't fix, so fail fast rather than burning all attempts.
  return /\b(timeout|timed out|network|fetch failed|failed to fetch)\b/i.test(
    error.message,
  );
};

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export interface RetryOptions {
  /** Total attempts, including the first. Defaults to 3. */
  maxAttempts?: number;
  /** Base backoff in ms; grows linearly (base * attempt). Defaults to 300. */
  backoffMs?: number;
  /** Retry predicate. Defaults to {@link isTransientRequestFailure}. */
  isRetryable?: (error: unknown) => boolean;
}

/**
 * Run an async operation with bounded retries and linear backoff. Retryable
 * failures (per `isRetryable`) are retried; anything else rethrows immediately.
 *
 * Idempotency: retries re-run `operation` verbatim, so only wrap operations
 * safe to repeat (GETs, or writes the server dedupes on a stable key). Never
 * wrap non-idempotent side effects like a payment capture.
 *
 * @example
 * const result = await withRetry(() =>
 *   httpPostWithStatus('/api/thing', body, { signRequest: true }),
 * );
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> => {
  const {
    maxAttempts = 3,
    backoffMs = 300,
    isRetryable = isTransientRequestFailure,
  } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error: unknown) {
      // Non-retryable failures rethrow immediately, without burning attempts.
      if (!isRetryable(error)) {
        throw error;
      }
      lastError = error;
      if (attempt < maxAttempts) {
        await delay(backoffMs * attempt);
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Operation failed after exhausting retries');
};
